import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import PageContainer from '../layout/PageContainer';
import { useState, SubmitEvent, useCallback, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import api from '../../api/axiosInstance';
import { emailRegex } from '../../utilities/EmailRegex';

export default function Register() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [emailInvalid, setEmailInvalid] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registrationSuccessful, setRegistrationSuccessful] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [reCaptchaProcessed, setReCaptchaProcessed] = useState(false);
  const [reCaptchaInvalid, setReCaptchaInvalid] = useState(false);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const onReCaptchaVerify = useCallback((value: string) => {
    setReCaptchaProcessed(true);

    if (!value) {
      setReCaptchaInvalid(true);
    }
  }, []);

  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;

    setEmail(email);

    setEmailInvalid(email.length <= 0 || !emailRegex.test(email));
  }, []);

  const handleRegistration = async (event: SubmitEvent) => {
    event.preventDefault();

    setRegistrationSuccessful(false);
    setRegistrationError('');

    if (reCaptchaProcessed &&
      !reCaptchaInvalid &&
      email &&
      firstName &&
      lastName) {

      setRegistering(true);

      try {
        const { data: result } = await api.post(
          '/register',
          {
            email: email,
            firstName: firstName,
            lastName: lastName
          });

        setRegistering(false);

        if (result.registrationSuccessful) {
          setRegistrationSuccessful(true);
        } else if (result.registrationUnsuccessfulReason) {
          setRegistrationError(result.registrationUnsuccessfulReason || '');
        }
      }
      catch (error: any) {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          setRegistrationError(error.message);
        }
      }

      setRegistering(false);
      setReCaptchaProcessed(false);
      setReCaptchaInvalid(false);
      setRefreshReCaptcha(!refreshReCaptcha);
    }
  };

  return <PageContainer
    title="Register"
  >
    <Box
      component="form"
      onSubmit={handleRegistration}
      noValidate
      autoComplete="off"
      sx={{ width: '100%', maxWidth: '500px' }}
    >
      <Box sx={{ width: '100%' }}>
        {registrationError ?
          <Alert severity="error">
            {registrationError}
          </Alert> :
          <></>
        }
        {reCaptchaProcessed && reCaptchaInvalid ?
          <Alert severity="error">
            reCAPTCHA failed.
          </Alert> :
          <></>
        }
        {registrationSuccessful ?
          <Alert severity="success">
            An email has been sent to the specified email address. Please click the link in the email to validate the specified email address.
          </Alert> :
          <></>
        }
        <Stack direction="column" spacing={2} sx={{ my: 2 }}>
          <TextField
            type="email"
            autoComplete="email"
            value={email}
            onChange={onEmailChange}
            name="email"
            label="Email Address"
            variant="outlined"
            error={errors.Email}
            helperText={errors.Email ? errors.Email[0] : ' '}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={registrationSuccessful}
            required
            fullWidth
          />
          <TextField
            autoComplete="given-name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            name="firstName"
            label="First Name"
            variant="outlined"
            error={errors.FirstName}
            helperText={errors.FirstName ? errors.FirstName[0] : ' '}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={registrationSuccessful}
            required
            fullWidth
          />
          <TextField
            autoComplete="family-name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            name="lastName"
            label="Last Name"
            variant="outlined"
            error={errors.LastName}
            helperText={errors.LastName ? errors.LastName[0] : ' '}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={registrationSuccessful}
            required
            fullWidth
          />
        </Stack>
        <Box sx={{ my: 2 }}>
          <GoogleReCaptcha
            onVerify={onReCaptchaVerify}
            refreshReCaptcha={refreshReCaptcha}
          />
        </Box>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            disabled={registering}
            onClick={() => navigate('/login')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            color="success"
            disabled={emailInvalid || !reCaptchaProcessed || reCaptchaInvalid || registrationSuccessful}
            loading={registering}
          >
            Register
          </Button>
        </Stack>
      </Box>
    </Box>
  </PageContainer>
}
